import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  User,
  FileText,
  Settings,
  FileCheck,
  DollarSign,
  AlertTriangle,
  Wrench,
  History,
  File,
  Download,
  Eye,
  Trash2,
  MessageSquare,
  Send,
} from "lucide-react";
import {
  useProjeto,
  useProjetoHistorico,
  useProjetoEquipamentos,
  useSinistrosDoProjeto,
  useServicosDoProjeto,
  usePedidosLimpezaDoProjeto,
} from "@/hooks/useProjetos";
import {
  useProjetoComentarios,
  useCriarProjetoComentario,
  useExcluirProjetoComentario,
} from "@/hooks/useProjetoComentarios";
import { EditarProjetoEquipamentosDialog } from "./EditarProjetoEquipamentosDialog";
import { ExcluirProjetoDialog } from "./ExcluirProjetoDialog";
import { useAuth } from "@/hooks/useAuth";
import { openStorageUrl } from "@/lib/storageUrl";
import { toast } from "sonner";

interface ProjetoDetalhesProps {
  projetoId: number;
  onProjetoExcluido?: () => void;
}

const formatMoeda = (valor?: number | null) => {
  if (valor == null || Number.isNaN(Number(valor))) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor));
};

const formatDataBr = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR");
};

/** Célula de aba com tamanho fixo (grid garante larguras iguais). */
const tabTriggerClass =
  "flex h-11 w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1 text-[10px] font-medium leading-tight whitespace-normal sm:flex-row sm:gap-1 sm:text-xs";

/** Altura fixa do painel — evita o modal mudar de tamanho ao trocar de aba. */
const tabPanelClass = "mt-4 h-[460px] overflow-y-auto space-y-4 pr-1";

const ABAS = [
  { value: "cliente", label: "Cliente", icon: User },
  { value: "orcamento", label: "Orçamento", icon: FileText },
  { value: "equipamento", label: "Equipamento", icon: Settings },
  { value: "contrato", label: "Contrato", icon: FileCheck },
  { value: "financeiro", label: "Financeiro", icon: DollarSign },
  { value: "documentos", label: "Documentos", icon: File },
  { value: "sinistros", label: "Sinistros", icon: AlertTriangle },
  { value: "servicos", label: "Serviços", icon: Wrench },
  { value: "comentarios", label: "Comentários", icon: MessageSquare },
  { value: "historico", label: "Histórico", icon: History },
] as const;

export function ProjetoDetalhes({ projetoId, onProjetoExcluido }: ProjetoDetalhesProps) {
  const { data: projeto, isLoading } = useProjeto(projetoId);
  const { data: historico } = useProjetoHistorico(projetoId);
  const { data: equipamentos, isLoading: isLoadingEquipamentos } = useProjetoEquipamentos(projetoId);
  const { data: sinistros = [], isLoading: isLoadingSinistros } = useSinistrosDoProjeto(projetoId);
  const { data: servicos = [], isLoading: isLoadingServicos } = useServicosDoProjeto(projetoId);
  const { data: limpezas = [] } = usePedidosLimpezaDoProjeto(projetoId);
  const { data: comentarios = [], isLoading: isLoadingComentarios } = useProjetoComentarios(projetoId);
  const criarComentario = useCriarProjetoComentario();
  const excluirComentario = useExcluirProjetoComentario();
  const { canEdit, isTi, canDelete, isAdmin } = useAuth();
  const [showEditarEquipamentos, setShowEditarEquipamentos] = useState(false);
  const [showExcluirDialog, setShowExcluirDialog] = useState(false);
  const [novoComentario, setNovoComentario] = useState("");
  const podeApagarComentario = isAdmin() || isTi();

  const handleEnviarComentario = async () => {
    if (!novoComentario.trim()) {
      toast.error("Escreva um comentário.");
      return;
    }
    try {
      await criarComentario.mutateAsync({ projetoId, conteudo: novoComentario });
      setNovoComentario("");
      toast.success("Comentário adicionado");
    } catch (e) {
      toast.error((e as Error)?.message || "Erro ao salvar comentário");
    }
  };

  const handleExcluirComentario = async (comentarioId: number) => {
    try {
      await excluirComentario.mutateAsync({ id: comentarioId, projetoId });
      toast.success("Comentário excluído");
    } catch (e) {
      toast.error((e as Error)?.message || "Erro ao excluir comentário");
    }
  };

  if (isLoading) {
    return <div className="p-4">Carregando...</div>;
  }

  if (!projeto) {
    return <div className="p-4">Projeto não encontrado</div>;
  }

  const documentos = [
    {
      key: "vistoria",
      label: "Vistoria",
      nome: projeto.vistoria_file_name,
      url: projeto.vistoria_file_url,
    },
    {
      key: "contrato",
      label: "Contrato (arquivo do projeto)",
      nome: projeto.contrato_file_name,
      url: projeto.contrato_file_url,
    },
    {
      key: "nf",
      label: "Nota fiscal",
      nome: projeto.nota_fiscal_file_name,
      url: projeto.nota_fiscal_file_url,
    },
    {
      key: "outros",
      label: "Outros documentos",
      nome: projeto.outros_documentos_name,
      url: projeto.outros_documentos_url,
    },
  ].filter((d) => d.url || d.nome);

  const planoNome =
    projeto.orcamento?.plano?.nome ||
    (projeto.orcamento?.plano_id ? `Plano #${projeto.orcamento.plano_id}` : null);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            {projeto.cliente?.nome || `Cliente ID: ${projeto.cliente_id}`}
          </h3>
          <p className="text-gray-600">Projeto ID: {projeto.id}</p>
          <div className="mt-2 text-sm text-gray-600">
            <span className="mr-3">Criado em: {formatDataBr(projeto.created_at)}</span>
          </div>
          {canDelete() && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              onClick={() => setShowExcluirDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir projeto e dados vinculados
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="cliente" className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-5 gap-1 rounded-md bg-muted p-1 lg:grid-cols-10">
          {ABAS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} className={tabTriggerClass}>
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="block w-full truncate text-center">{label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="cliente" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Informações do Cliente</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Nome</label>
                <p className="text-gray-900">{projeto.cliente?.nome || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-gray-900">{projeto.cliente?.email || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Telefone</label>
                <p className="text-gray-900">{projeto.cliente?.telefone || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">CPF/CNPJ</label>
                <p className="text-gray-900">{projeto.cliente?.cpf_cnpj || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Valor do Projeto</label>
                <p className="text-gray-900 font-medium">{formatMoeda(projeto.valor_projeto)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Observações</label>
                <p className="text-gray-900 whitespace-pre-wrap">{projeto.observacoes || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Consultor</label>
                <p className="text-gray-900">{projeto.consultor_nome || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Contato do Consultor</label>
                <p className="text-gray-900">{projeto.consultor_celular || projeto.consultor_email || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Empresa responsável</label>
                <p className="mt-0.5">
                  {projeto.empresa_responsavel ? (
                    <Badge className="bg-orange-400 text-white hover:bg-orange-400 border-transparent font-medium">
                      {projeto.empresa_responsavel}
                    </Badge>
                  ) : (
                    <span className="text-gray-900">—</span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">ID do projeto na empresa</label>
                <p className="mt-0.5">
                  {projeto.empresa_projeto_externo_id || projeto.id_projeto_elevo ? (
                    <Badge className="bg-orange-400 text-white hover:bg-orange-400 border-transparent font-medium">
                      {projeto.empresa_projeto_externo_id || projeto.id_projeto_elevo}
                    </Badge>
                  ) : (
                    <span className="text-gray-900">—</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dados do Vendedor</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Nome</label>
                <p className="text-gray-900">{projeto.vendedor_nome || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Celular</label>
                <p className="text-gray-900">{projeto.vendedor_celular || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">CPF/CNPJ</label>
                <p className="text-gray-900">{projeto.vendedor_cpf_cnpj || "—"}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orcamento" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Informações do Orçamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Valor do Projeto</label>
                  <p className="text-gray-900 font-medium">{formatMoeda(projeto.valor_projeto)}</p>
                </div>
                {projeto.orcamento ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Valor do Seguro</label>
                      <p className="text-gray-900 font-medium">
                        {formatMoeda(projeto.orcamento.valor_seguro)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Plano escolhido</label>
                      <p className="text-gray-900 font-medium">{planoNome || "—"}</p>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Orçamento</label>
                    <p className="text-gray-500">Ainda não gerado</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="equipamento" className={tabPanelClass}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Equipamentos do Projeto</CardTitle>
              {(canEdit() || isTi()) && (
                <Button
                  onClick={() => setShowEditarEquipamentos(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Editar Equipamentos
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isLoadingEquipamentos ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                  <span className="ml-2">Carregando equipamentos...</span>
                </div>
              ) : equipamentos && equipamentos.length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-3">
                    {(equipamentos as any[]).map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-gray-900">
                            {item.equipamento?.marca} {item.equipamento?.modelo}
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {item.equipamento?.tipo}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Quantidade:</span>
                            <span className="ml-1 font-medium">{item.quantidade}</span>
                          </div>
                          {item.equipamento?.potencia && (
                            <div>
                              <span className="text-gray-600">Potência:</span>
                              <span className="ml-1 font-medium">{item.equipamento.potencia}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-600">Preço Unitário:</span>
                            <span className="ml-1 font-medium">
                              {formatMoeda(item.equipamento?.preco_unitario)}
                            </span>
                          </div>
                        </div>
                        {item.observacoes && (
                          <div className="mt-2">
                            <span className="text-gray-600 text-sm">Observações:</span>
                            <p className="text-sm text-gray-700 mt-1">{item.observacoes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-green-800 font-medium">Total de Equipamentos:</span>
                      <span className="text-green-800 font-bold">
                        {equipamentos.reduce((total, item) => total + item.quantidade, 0)} itens
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Nenhum equipamento adicionado a este projeto</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Os equipamentos cadastrados na criação aparecem aqui.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dados da Instalação</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Empresa Responsável</label>
                  <p className="mt-0.5">
                    {projeto.empresa_responsavel ? (
                      <Badge className="bg-orange-400 text-white hover:bg-orange-400 border-transparent font-medium">
                        {projeto.empresa_responsavel}
                      </Badge>
                    ) : (
                      <span className="text-gray-900">—</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">CNPJ</label>
                  <p className="text-gray-900">{projeto.cnpj_empresa || "—"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Instalador</label>
                  <p className="text-gray-900">{projeto.instalador_nome || "—"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Contato do Instalador</label>
                  <p className="text-gray-900">{projeto.instalador_contato || "—"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">ID do projeto na empresa</label>
                  <p className="mt-0.5">
                    {projeto.empresa_projeto_externo_id || projeto.id_projeto_elevo ? (
                      <Badge className="bg-orange-400 text-white hover:bg-orange-400 border-transparent font-medium">
                        {projeto.empresa_projeto_externo_id || projeto.id_projeto_elevo}
                      </Badge>
                    ) : (
                      <span className="text-gray-900">—</span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contrato" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Informações do Contrato</CardTitle>
            </CardHeader>
            <CardContent>
              {projeto.contrato ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Número</label>
                    <p className="text-gray-900">{projeto.contrato.numero_contrato || `#${projeto.contrato.id}`}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <p className="text-gray-900">{projeto.contrato.status || "—"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Valor</label>
                    <p className="text-gray-900 font-medium">{formatMoeda(projeto.contrato.valor)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Criado em</label>
                    <p className="text-gray-900">{formatDataBr(projeto.contrato.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Início</label>
                    <p className="text-gray-900">{formatDataBr(projeto.contrato.data_inicio)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Fim / Vigência</label>
                    <p className="text-gray-900">{formatDataBr(projeto.contrato.data_fim)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Data de Assinatura</label>
                    <p className="text-gray-900">
                      {projeto.contrato.data_assinatura
                        ? formatDataBr(projeto.contrato.data_assinatura)
                        : "Não assinado"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Arquivo PDF</label>
                    {projeto.contrato.arquivo_pdf_url ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1"
                        onClick={() => void openStorageUrl(projeto.contrato!.arquivo_pdf_url!)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Abrir PDF
                      </Button>
                    ) : (
                      <p className="text-gray-900">Não anexado</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-gray-600 text-sm">
                    Nenhum contrato vinculado ainda. O contrato aparece aqui após a aprovação do
                    orçamento / criação do pedido.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financeiro" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Informações Financeiras</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Valor do Projeto</label>
                  <p className="text-gray-900 font-medium">{formatMoeda(projeto.valor_projeto)}</p>
                  <p className="text-xs text-gray-500 mt-1">Da criação do projeto</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Valor do Orçamento</label>
                  <p className="text-gray-900 font-medium">
                    {projeto.orcamento ? formatMoeda(projeto.orcamento.valor_total) : "—"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Valor do Seguro</label>
                  <p className="text-gray-900 font-medium">
                    {projeto.orcamento ? formatMoeda(projeto.orcamento.valor_seguro) : "—"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Valor do Contrato</label>
                  <p className="text-gray-900 font-medium">
                    {projeto.contrato ? formatMoeda(projeto.contrato.valor) : "—"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Status do Pagamento</label>
                  <p className="text-gray-900">
                    {projeto.contrato?.status === "ativo"
                      ? "Em andamento"
                      : projeto.contrato?.status || "Pendente (após criação do contrato)"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Data de Aprovação</label>
                  <p className="text-gray-900">{formatDataBr(projeto.data_aprovacao)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documentos" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Documentos do Projeto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {documentos.length > 0 ? (
                  documentos.map((doc) => (
                    <div key={doc.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <File className="h-5 w-5 text-green-600 shrink-0" />
                          <div className="min-w-0">
                            <h4 className="font-medium text-gray-900">{doc.label}</h4>
                            <p className="text-sm text-gray-600 truncate">
                              {doc.nome || "Arquivo anexado"}
                            </p>
                          </div>
                        </div>
                        {doc.url && (
                          <div className="flex gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void openStorageUrl(doc.url!)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = doc.url!;
                                link.download = doc.nome || `${doc.key}.pdf`;
                                link.click();
                              }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Baixar
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                    <p className="text-gray-600 text-sm">
                      Nenhum documento anexado na criação do projeto (vistoria, contrato, NF, etc.).
                    </p>
                  </div>
                )}

                {projeto.contrato?.arquivo_pdf_url && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FileCheck className="h-5 w-5 text-green-600" />
                        <div>
                          <h4 className="font-medium text-gray-900">PDF do contrato vinculado</h4>
                          <p className="text-sm text-gray-600">Gerado após criação do contrato</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void openStorageUrl(projeto.contrato!.arquivo_pdf_url!)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Abrir
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sinistros" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Sinistros do Projeto</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingSinistros ? (
                <p className="text-gray-500 text-sm">Carregando sinistros...</p>
              ) : sinistros.length > 0 ? (
                <div className="space-y-3">
                  {sinistros.map((s) => (
                    <div key={s.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-medium text-gray-900">
                          {s.tipo || `Sinistro #${s.id}`}
                        </div>
                        <Badge variant="outline">{s.status || "—"}</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                        <div>Ocorrência: {formatDataBr(s.data_ocorrencia)}</div>
                        <div>Estimado: {formatMoeda(s.valor_estimado)}</div>
                        <div>Aprovado: {formatMoeda(s.valor_aprovado)}</div>
                      </div>
                      {s.descricao && <p className="text-sm text-gray-700 mt-2">{s.descricao}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-gray-600 text-sm">Nenhum sinistro vinculado a este projeto.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="servicos" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Serviços</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingServicos ? (
                <p className="text-gray-500 text-sm">Carregando serviços...</p>
              ) : servicos.length === 0 && limpezas.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-gray-600 text-sm">Nenhum serviço registrado para este projeto.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {servicos.map((s) => (
                    <div key={`svc-${s.id}`} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{s.tipo_servico || `Serviço #${s.id}`}</span>
                        <Badge variant="outline">{s.status}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Solicitado: {formatDataBr(s.data_solicitacao || s.created_at)} · Agendado:{" "}
                        {formatDataBr(s.data_agendada)}
                      </p>
                    </div>
                  ))}
                  {limpezas.map((l) => (
                    <div key={`limp-${l.id}`} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">
                          Limpeza — pedido {l.numero_pedido || `#${l.id}`}
                        </span>
                        <Badge variant="outline">{l.limpeza_status || "pendente"}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Agendada: {formatDataBr(l.limpeza_data_agendada)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comentarios" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Comentários
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  placeholder="Escreva um comentário sobre este projeto..."
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleEnviarComentario}
                    disabled={criarComentario.isPending || !novoComentario.trim()}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {criarComentario.isPending ? "Enviando..." : "Comentar"}
                  </Button>
                </div>
              </div>

              {isLoadingComentarios ? (
                <p className="text-sm text-gray-500">Carregando comentários...</p>
              ) : comentarios.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-sm text-gray-600">Nenhum comentário ainda.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {comentarios.map((c) => (
                    <div key={c.id} className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {c.usuario?.nome || c.usuario?.email || "Usuário"}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            {formatDataBr(c.created_at)}{" "}
                            {new Date(c.created_at).toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          {podeApagarComentario && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Excluir comentário"
                              disabled={excluirComentario.isPending}
                              onClick={() => handleExcluirComentario(c.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.conteudo}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico" className={tabPanelClass}>
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {historico?.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-2" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {item.status_anterior && `${item.status_anterior} → `}
                          {item.status_novo}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDataBr(item.created_at)} às{" "}
                          {new Date(item.created_at).toLocaleTimeString("pt-BR")}
                        </span>
                      </div>
                      {item.observacoes && (
                        <p className="text-sm text-gray-600">{item.observacoes}</p>
                      )}
                    </div>
                  </div>
                ))}

                {historico && historico.length === 0 && (
                  <p className="text-gray-500">Nenhum histórico encontrado</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EditarProjetoEquipamentosDialog
        projetoId={projeto.id}
        open={showEditarEquipamentos}
        onOpenChange={setShowEditarEquipamentos}
      />
      <ExcluirProjetoDialog
        projetoId={projeto.id}
        open={showExcluirDialog}
        onOpenChange={setShowExcluirDialog}
        onSuccess={onProjetoExcluido}
      />
    </div>
  );
}
